import { EventEmitter, inject, Injectable } from '@angular/core';
import { Filter, Order, OrderType, Paginator, Search } from '@docpath/linces-angular-components/common';
import { Table, TableField, TableFieldIcon, TableFieldIcons, TableFieldLink, TableFieldText, TableRow } from '@docpath/webcomponents';
import { faCircle } from '@fortawesome/free-solid-svg-icons';

import { IndexService, IndexServiceStatus } from '../../models/index-service';
import { IndexServicesFormService } from '../../services/index-services-form.service';
import { IndexServicesHttpService } from '../../services/index-services-http.service';

@Injectable({
    providedIn: 'root'
})
export class IndexServicesTableService {

    indexServicesLoaded: EventEmitter<IndexService[]> = new EventEmitter();

    private readonly i18nPrefix = 'services-config.index-services';
    private currentPage: number;
    private elements: number;
    private currentSize: number;
    private indexServices: IndexService[] = [];

    private readonly indexServicesHttpService = inject(IndexServicesHttpService);
    private readonly indexServicesFormService = inject(IndexServicesFormService);

    constructor() {
        this.currentPage = 1;
        this.elements = 0;
        this.currentSize = 25;
    }

    getCurrentPage(): number {
        return this.currentPage;
    }

    getElements(): number {
        return this.elements;
    }

    getIndexServices(): IndexService[] {
        return this.indexServices;
    }

    getSelectedIndexServices(table: Table): IndexService[] {
        return table.body.filter(row => row.selected).map(row => row.content);
    }

    loadIndexServices(filters: Filter[], page = this.currentPage, size = this.currentSize): void {
        this.currentPage = page;
        this.currentSize = size;

        const search = new Search(filters, [new Order('name', OrderType.Asc)], [],
            new Paginator(page - 1, this.currentSize, this.elements));

        this.indexServicesHttpService.getIndexServices(search, (result, totalSize) => {
            this.elements = totalSize;
            this.indexServices = result as IndexService[];
            this.indexServicesLoaded.emit(result);
        });

    }

    initTable(): Table {
        const header = this.initTableHeader();
        const body = this.initTableBody([]);

        return new Table(header, body);
    }

    initTableHeader(): TableRow {
        return new TableRow(
            [
                new TableFieldText(`${this.i18nPrefix}.table.name`, undefined, undefined, undefined, undefined, true),
                new TableFieldText(`${this.i18nPrefix}.table.url`, undefined, undefined, undefined, undefined, true),
                new TableFieldText(`${this.i18nPrefix}.table.description`, undefined, undefined, undefined, undefined, true),
                new TableFieldText(`${this.i18nPrefix}.table.status.label`, undefined, { style: 'text-align: center;' }, undefined, undefined, true)
            ]
        );
    }

    initTableBody(indexServices: IndexService[]): TableRow[] {
        return indexServices.map(indexService => {
            const fields: TableField[] = [],
                nameClickAction = () => this.indexServicesFormService.openEditIndexServiceForm(indexService);
            fields.push(new TableFieldLink(indexService.name, indexService.name, undefined, nameClickAction, indexService.name));
            fields.push(new TableFieldText(indexService.url, undefined, undefined));
            fields.push(new TableFieldText(indexService.description ?? '', undefined, undefined));
            fields.push(this.getStatusField(indexService));
            const tableRow = new TableRow(fields);
            tableRow.content = indexService;

            return tableRow;
        });
    }

    private getStatusField(indexService: IndexService): TableFieldIcons {
        let statusIcon = null;
        if (indexService.status === IndexServiceStatus.Connected) {
            statusIcon = new TableFieldIcon(faCircle, `${this.i18nPrefix}.table.status.connected`, 'lg', undefined, undefined, { cssClass: 'text-dpgreen' });
        } else if (indexService.status === IndexServiceStatus.NotConnected) {
            statusIcon = new TableFieldIcon(faCircle, `${this.i18nPrefix}.table.status.not-connected`, 'lg', undefined, undefined, { cssClass: 'text-dpred' });
        } else {
            statusIcon = new TableFieldIcon(faCircle, `${this.i18nPrefix}.table.status.connected-with-errors`, 'lg', undefined, undefined, { cssClass: 'text-dpyellow' });
        }

        return new TableFieldIcons([statusIcon], { style: 'text-align: center;' });
    }

}
